import { setBooleanFeatureFlagResolver } from '@atlaskit/platform-feature-flags';
import { envType } from '@atlassiansox/analytics-web-client';
import { initializeAnalyticsClient } from '@databases/analytics';
import { getBaseUrl } from '@databases/database-state';
import { DatabaseContentContext, setCloudId } from '@databases/database-state';
import { StatsigClient } from '@databases/feature-flags/client/statsig';
import { loadConfluenceFlags } from '@databases/feature-flags/confluenceFlags';
import { getFeatureFlagsInUse } from '@databases/feature-flags/manager';
import { featureFlagClientManager } from '@databases/feature-flags/manager';
import CommonApp from '@databases/frontend/App';
import DatabaseDetails from '@databases/frontend/DatabaseDetails';
import { appStore } from '@databases/redux/store';
import { setContext } from '@databases/utils/redux/context/actions';
import { withoutThrottledBatching } from '@databases/utils/redux/redux';
import { setAppMode } from '@databases/utils/utils';
import React, { useEffect, useLayoutEffect, useMemo, useState } from 'react';

import { currentUserState } from '@canvas/canvas-state';
import { setIdentity } from '@canvas/connection/identity';
import { type CanvasIdentityOld, IdentityKind } from '@canvas/model/identity';
import * as Sentry from '@canvas/sentry-react';

const App = ({
  config: {
    cloudId,
    spaceId,
    spaceKey,
    userId,
    contentId,
    activationId,
    contentReferenceId: referenceId,
    spaceName,
    title,
    identity,
  },
}: {
  config: {
    cloudId: string;
    spaceId: string;
    spaceKey: string;
    userId: string;
    contentId: string;
    activationId: string;
    contentReferenceId: string;
    spaceName: string;
    title: string;
    identity: CanvasIdentityOld;
  };
}) => {
  setAppMode('STANDALONE');
  useEffect(() => {
    Sentry.getCurrentScope().setTag(
      'cloudId',
      ENVTYPE === envType.LOCAL || !ENVTYPE ? 'local' : cloudId
    );
  }, [cloudId]);
  useEffect(() => {
    Sentry.setUser({ id: userId });
  }, [userId]);
  useEffect(() => {
    setCloudId(cloudId);
  }, [cloudId]);
  useEffect(() => {
    setIdentity({
      ...identity,
      type: IdentityKind.CanvasId,
    });
    currentUserState.status = 'succeeded';
    currentUserState.identity = {
      ...identity,
      type: IdentityKind.CanvasId,
    };
  }, [identity]);
  const [analyticsClientLoaded, setAnalyticsClientLoaded] = useState(false);
  useEffect(() => {
    const loadAnalyticsClient = async () => {
      await initializeAnalyticsClient(
        ENVTYPE === envType.LOCAL || !ENVTYPE ? undefined : cloudId,
        spaceId,
        userId,
        ENVTYPE ?? envType.LOCAL,
        SENTRY_RELEASE
      );
      setAnalyticsClientLoaded(true);
    };
    loadAnalyticsClient();
  }, [cloudId, spaceId, userId]);
  const [databaseFlagsLoaded, setDatabaseFlagsLoaded] = useState(false);
  useEffect(() => {
    const loadFeatureFlags = async () => {
      setBooleanFeatureFlagResolver((flagKey) => {
        return [
          'platform.linking-platform.datasource.show-jlol-basic-filters',
          'platform.linking-platform.datasource.show-clol-basic-filters',
        ].includes(flagKey);
      });
      featureFlagClientManager.add(new StatsigClient(BUILD_MODE, cloudId, userId));
      await featureFlagClientManager.initialize();

      const featureFlagsInUse = getFeatureFlagsInUse();
      if (featureFlagsInUse.length > 0) {
        Sentry.setContext('Feature Flags', {
          'In Use': featureFlagsInUse,
        });
      }
      setDatabaseFlagsLoaded(true);
    };
    loadFeatureFlags();
  }, [cloudId, userId]);
  const [confluenceFlagsLoaded, setConfluenceFlagsLoaded] = useState(false);
  useEffect(() => {
    const loadConfluenceFlagsAndMarkDone = async () => {
      await loadConfluenceFlags();
      setConfluenceFlagsLoaded(true);
    };
    loadConfluenceFlagsAndMarkDone();
  }, []);
  useLayoutEffect(() => {
    // Focus iframe to enable keyboard shortcuts
    window.focus();
  }, []);
  const [storeInitialized, setStoreInitialized] = useState(false);
  useEffect(() => {
    // eslint-disable-next-line require-await
    withoutThrottledBatching(async () => {
      appStore.dispatch(
        setContext({
          space: {
            key: spaceKey,
          },
          baseUrl: getBaseUrl(),
          content: undefined,
          userId,
        })
      );
      setStoreInitialized(true);
    });
  }, [spaceKey, userId]);
  const queryParams = useMemo(
    // eslint-disable-next-line no-restricted-syntax
    () => new URLSearchParams(window.parent.location.search),
    []
  );
  const contentContext = useMemo(
    () => ({
      contentId,
      referencing: false,
      activationId,
      cloudId,
      setContentId: () => {}, // used by referencing dialog, but not used in standalone (where confluence defines the contentId)
      spaceKey,
      savedViewId: queryParams.get('savedViewId'),
      unsavedViewId: queryParams.get('unsavedView'),
      entryId: queryParams.get('entryId'),
      fieldId: queryParams.get('fieldId'),
      referenceId,
      spaceId,
      parentContentId: contentId,
      spaceName,
      title,
    }),
    [
      contentId,
      activationId,
      cloudId,
      spaceKey,
      referenceId,
      spaceId,
      spaceName,
      title,
      queryParams,
    ]
  );

  if (
    !analyticsClientLoaded ||
    !databaseFlagsLoaded ||
    !confluenceFlagsLoaded ||
    !storeInitialized
  ) {
    return null;
  }

  return (
    <DatabaseContentContext.Provider value={contentContext}>
      <CommonApp aaid={userId}>
        <DatabaseDetails />
      </CommonApp>
    </DatabaseContentContext.Provider>
  );
};

export default App;
